import {
  Component, Output, EventEmitter, Input, ViewEncapsulation, OnChanges, SimpleChanges, OnInit, ViewChild,
} from '@angular/core';
import { BehaviorSubject, Observable, Subject, combineLatest, firstValueFrom } from 'rxjs';
import { fadeInOut } from 'src/animations';
import { ISearchActivity } from 'typings/doenkids/doenkids';
import { DoenkidsSessionProvider } from 'src/providers/session.provider';
import { OrganizationUnitListService } from 'src/api/customer/organization-unit-list/organization-unit-list.service';
import { UserListService } from 'src/api/customer/users-list/user-list.service';
import { PermissionProvider } from 'src/providers/permission.provider';
import { filter, map, takeUntil } from 'rxjs/operators';
import { ActivityHighLightService } from 'src/api/activity/activity-highlight/activity-highlight.service';
import { MatDialog } from '@angular/material/dialog';
import {
  AddActivityToPinbordFolderDialogComponent,
} from 'src/components/dialogs/add-activity-to-pinbord-folder-dialog/add-activity-to-pinbord-folder-dialog.component';
import { PinbordProvider } from 'src/providers/pinbord.provider';
import { MatMenuTrigger } from '@angular/material/menu';
import { marker as _ } from '@biesbjerg/ngx-translate-extract-marker';
import {
  RemoveActivityFromPinbordFolderDialogComponent,
} from 'src/components/dialogs/remove-activity-from-pinbord-folder-dialog/remove-activity-from-pinbord-folder-dialog.component';
import { isNil } from 'lodash';
import { AddActivityImprovedDialogComponent } from 'src/components/dialogs/add-activity-improved/add-activity-improved-dialog.component';
import { ManageActivityPendingRemovalComponent } from 'src/components/dialogs/manage-activity-pending-removal/manage-activity-pending-removal.component';
import dayjs from 'dayjs';

type ActivityAction = 'pin' | 'unpin' | 'highlight' | 'unHighlight' | 'setRenewal' | 'setRemoval';
_('activity.card.action.pin');
_('activity.card.action.unpin');
_('activity.card.action.highlight');
_('activity.card.action.unHighlight');
_('activity.card.action.setRenewal');

@Component({
    selector: 'app-activity-card',
    templateUrl: './activity-card.component.html',
    styleUrls: ['./activity-card.component.scss'],
    encapsulation: ViewEncapsulation.None,
    animations: [fadeInOut],
    standalone: false
})
export class ActivityCardComponent implements OnInit, OnChanges {
  private stop$ = new Subject<void>();

  private _activity: ISearchActivity;

  @Input() set activity(activity: ISearchActivity) {
    this._activity = activity;
    this.activity$.next(activity);
  }

  @ViewChild(MatMenuTrigger) actionsMenu: MatMenuTrigger;

  get activity(): ISearchActivity {
    return this._activity;
  }

  private activity$ = new BehaviorSubject<ISearchActivity>(null);

  public isOrganizationOfTypeLocation$: Observable<boolean>;

  public isOrganizationOfTypeGroup$: Observable<boolean>;

  public currentOUId$: Observable<number>;

  public currentOUNodePath$: Observable<number[]>;

  public isAdmin$: Observable<boolean>;

  public hasWritePermissionOnAtLeastOneCustomerOUInCurrentNodeTree$: Observable<boolean>;

  public hasOUWritePermission$: Observable<boolean>;

  public isOnAPinbord = false;

  public activityActions$ = new BehaviorSubject<ActivityAction[]>([]);

  public isReader$: Observable<boolean>;

  public activityIsHighlighted$: Observable<boolean>;

  public activityIsHighlighedByCurrentOU$: Observable<boolean>;

  public activityHighlightingIsEnabled$: Observable<boolean>;

  public isImproved$ = this.activity$.pipe(
    takeUntil(this.stop$),
    map((activity) => activity?.improved_activities?.some((improvedActivity) => {
      const showUntil = improvedActivity.show_until;
      if (!showUntil) return false;

      const showUntilDate = new Date(showUntil);
      // Set the time to 23:59:59
      showUntilDate.setHours(23, 59, 59, 999);

      return showUntilDate > new Date();
    })),
  );

  public isPendingRemoval$ = this.activity$.pipe(
    takeUntil(this.stop$),
    map((activity) => activity?.pending_removal_activities?.length > 0),
  );

  public isNew$ = this.activity$.pipe(
    takeUntil(this.stop$),
    map((activity) => {
      if (!activity?.published_at) return false;

      // Check if the activity was published within the last 6 weeks
      const publishedAt = dayjs(activity.published_at);
      const sixWeeksAgo = dayjs().subtract(6, 'week');
      return publishedAt.isAfter(sixWeeksAgo);
    }),
  );

  public isOutdated$ = this.activity$.pipe(
    takeUntil(this.stop$),
    map((activity) => {
      // If this is a copy (has replace_activity_id) and original data is available
      if (activity?.replace_activity_id && activity?.replaced_activity_updated_at) {
        // If country codes are different, this is a translation - don't show outdated banner
        if (activity.country_code?.toLowerCase() !== activity.replaced_activity_country_code?.toLowerCase()) {
          return false;
        }

        // Compare original's update timestamp against copy's creation timestamp
        // If original was updated after this copy was created, then copy is outdated
        const copyCreatedAt = new Date(activity.created_at).getTime();
        const originalUpdatedAt = new Date(activity.replaced_activity_updated_at).getTime();

        return originalUpdatedAt > copyCreatedAt;
      }

      return false;
    }),
  );

  public isOriginalDeleted$ = this.activity$.pipe(
    takeUntil(this.stop$),
    map((activity) => {
      // Original deleted flag provided by search results
      if (activity?.replace_activity_id && typeof activity?.replaced_activity_archived === 'boolean') {
        return activity.replaced_activity_archived === true;
      }
      return false;
    }),
  );

  public isOriginalUpdatedOnly$ = this.activity$.pipe(
    takeUntil(this.stop$),
    map((activity) => {
      if (!activity?.replace_activity_id) return false;
      if (activity?.replaced_activity_archived === true) return false;
      if (!activity?.replaced_activity_updated) return false;

      const dismissedAt = activity?.original_updated_last_dismissed_at;
      const originalUpdatedAt = activity?.replaced_activity_updated_at;
      const isDismissed = !!(dismissedAt && originalUpdatedAt
        && new Date(dismissedAt).getTime() >= new Date(originalUpdatedAt).getTime());

      return !isDismissed;
    }),
  );


  /**
   * The name of the user that originally created the activity
   *
   */
  public activityCreatedByUser = '';

  /**
   * The name of the organization unit that originally created the activity
   *
   */
  public activityCreatedByOU = '';

  public shouldShowCountryFlag$: Observable<boolean>;

  public shouldShowActivityIsCopy$: Observable<boolean>;

  public isDKCountryRoot$: Observable<boolean>;

  @Output() loaded = new EventEmitter<void>();

  constructor(
    private dialog: MatDialog,
    private $session: DoenkidsSessionProvider,
    private $permission: PermissionProvider,
    private organizationUnitListService: OrganizationUnitListService,
    private userListService: UserListService,
    private $activityHighLight: ActivityHighLightService,
    private $pinbord: PinbordProvider,
  ) {
    this.isOrganizationOfTypeLocation$ = this.$session.isCurrentOrganizationUnitIsOfTypeLocation$.pipe(takeUntil(this.stop$));
    this.isOrganizationOfTypeGroup$ = this.$session.isCurrentOrganizationUnitIsOfTypeGroup$.pipe(takeUntil(this.stop$));
    this.currentOUId$ = this.$session.currentOuId$.pipe(takeUntil(this.stop$));
    this.currentOUNodePath$ = this.$session.getOrganizationUnit$.pipe(takeUntil(this.stop$), map((ou) => ou.node_path.map((node) => parseInt(`${node}`, 10))));
    this.isAdmin$ = this.$session.isAdmin$.pipe(takeUntil(this.stop$));
    this.isReader$ = this.$session.isReader$.pipe(takeUntil(this.stop$));
    this.hasWritePermissionOnAtLeastOneCustomerOUInCurrentNodeTree$ = this.$permission.hasWritePermissionOnAtLeastOneCustomerOUInCurrentNodeTree$.pipe(takeUntil(this.stop$));
    this.hasOUWritePermission$ = this.$permission.hasOUWritePermissions$.pipe(takeUntil(this.stop$));

    this.activityIsHighlighted$ = combineLatest([this.activity$, this.currentOUNodePath$]).pipe(takeUntil(this.stop$), map(([activity, nodePath]) =>
      activity?.highlighted_organization_unit_id?.some((highlightOuId) => nodePath.includes(highlightOuId)) ?? false,
    ));

    this.activityIsHighlighedByCurrentOU$ = combineLatest([this.activity$, this.currentOUId$]).pipe(takeUntil(this.stop$), map(([activity, currentOuId]) =>
      activity?.highlighted_organization_unit_id?.includes(currentOuId) ?? false,
    ));

    this.activityHighlightingIsEnabled$ = combineLatest([this.isAdmin$, this.hasOUWritePermission$, this.hasWritePermissionOnAtLeastOneCustomerOUInCurrentNodeTree$, this.isReader$]).pipe(
      takeUntil(this.stop$),
      map(([isAdmin, hasOUWritePermission, hasWritePermissionOnAtLeastOneCustomerOUInCurrentNodeTree, isReader]) =>
        isAdmin || (!isReader && hasOUWritePermission && hasWritePermissionOnAtLeastOneCustomerOUInCurrentNodeTree),
      ),
    );

    this.shouldShowCountryFlag$ = this.$session.ouLanguages$.pipe(
      takeUntil(this.stop$),
      map((languages) => languages.length > 1),
    );

    this.shouldShowActivityIsCopy$ = combineLatest([this.isOrganizationOfTypeLocation$, this.isOrganizationOfTypeGroup$, this.activity$]).pipe(
      takeUntil(this.stop$),
      filter(([isLocation, isGroup, activityDetails]) => !isNil(isLocation) && !isNil(isGroup) && !isNil(activityDetails)),
      map(([isLocation, isGroup, activityDetails]) => {
        return !((isLocation || isGroup)) &&
          (!isNil(activityDetails.replace_activity_id) && !isNil(activityDetails.replaced_activity_country_code)
          && activityDetails.replaced_activity_country_code.toLowerCase() === activityDetails.country_code.toLowerCase());
      }),
    );

    this.isDKCountryRoot$ = this.$session.getOrganizationUnit$.pipe(
      takeUntil(this.stop$),
      map((ou) => !isNil((ou as any)?.dk_country_root)),
    );
  }

  async ngOnInit() {
    if (this.activity) {
      this.setActivityCreatedByValues(this.activity);
      await this.setIsOnPinbord(this.activity);
      this.setHighlightActions();
      await this.setPinbordActions();
    }

    this.$pinbord.currentFolders$.pipe(takeUntil(this.stop$)).subscribe(async () => {
      await this.setIsOnPinbord();
      await this.setPinbordActions();
    });
  }

  async ngOnChanges(changes: SimpleChanges) {
    if (changes.activity && !changes.activity.firstChange) {
      this.setActivityCreatedByValues(changes.activity.currentValue);
      await this.setIsOnPinbord(changes.activity.currentValue);
      this.setHighlightActions();
      await this.setPinbordActions();
    }
  }

  imageLoaded() {
    this.loaded.next();
  }

  async setActivityCreatedByValues(activity: ISearchActivity) {
    if (activity.status_id === 2) {
      if (activity.created_by_organization_unit_id) {
        try {
          // eslint-disable-next-line no-await-in-loop
          const organization = await this.organizationUnitListService.fetch(activity.created_by_organization_unit_id);

          this.activityCreatedByOU = organization.name;
        } catch (errorResponse) {
          // There was an error fetching the ou but since its not critical to show this we supress the error and set the name to blank to be sure
          //
          this.activityCreatedByOU = '';
        }
      }

      if (activity.created_by_user_id) {
        // fetch user
        try {
          // eslint-disable-next-line no-await-in-loop
          const user = await this.userListService.fetch(activity.created_by_user_id);

          this.activityCreatedByUser = user.email;
        } catch (errorResponse) {
          // There was an error fetching the user but since its not critical to show this we supress the error and set the name to blank to be sure
          //
          this.activityCreatedByUser = '';
        }
      }
    } else {
      this.activityCreatedByUser = '';
      this.activityCreatedByOU = '';
    }
  }

  async setPinbordActions() {
    const ouIsLocation = await firstValueFrom(this.isOrganizationOfTypeLocation$);

    if (!ouIsLocation) {
      return;
    }
    const isReader = await firstValueFrom(this.isReader$);
    const currentFoldersLength = ((await firstValueFrom(this.$pinbord.currentFolders$))?.folders ?? []).length;
    const currentActions: ActivityAction[] = [...this.activityActions$.value];
    const newActions = currentActions.filter((action) => !['pin', 'unpin'].includes(action));

    if (isReader) {
      this.activityActions$.next(newActions);
      return;
    }

    if (this.isOnAPinbord) {
      newActions.unshift('unpin');
      if (currentFoldersLength > 1) {
        newActions.unshift('pin');
      }
    } else {
      newActions.unshift('pin');
    }

    this.activityActions$.next(newActions);
  }

  async setIsOnPinbord(activity?: ISearchActivity) {
    const currentFolders = (await firstValueFrom(this.$pinbord.currentFolders$))?.folders ?? [];
    const activityToFind = activity ?? this.activity;

    if (currentFolders.find((folder) => folder.activities.find((folderActivity) => folderActivity?.id === activityToFind.id))) {
      this.isOnAPinbord = true;
    } else {
      this.isOnAPinbord = false;
    }
  }

  async setHighlightActions() {
    const activityHighlightingIsEnabled = await firstValueFrom(this.activityHighlightingIsEnabled$);
    const currentActions = [...this.activityActions$.value];
    const newActions = currentActions.filter((action) => !['highlight', 'unHighlight', 'setRenewal', 'setRemoval'].includes(action));

    if (!activityHighlightingIsEnabled) {
      this.activityActions$.next(newActions);
      return;
    }

    const activityIsHighLighted = await firstValueFrom(this.activityIsHighlighted$);
    const activityIsHighlightedByCurrentOU = await firstValueFrom(this.activityIsHighlighedByCurrentOU$);

    if (activityIsHighLighted && activityIsHighlightedByCurrentOU) {
      newActions.push('unHighlight');
    }

    if (!activityIsHighLighted) {
      newActions.push('highlight');
    }

    const isAdmin = await firstValueFrom(this.isAdmin$);

    if (isAdmin) {
      newActions.push('setRenewal');
      newActions.push('setRemoval');
    }

    this.activityActions$.next([...new Set(newActions)]);
  }

  openActionsMenu($event: any) {
    $event.preventDefault();
    $event.stopPropagation();

    if (this.actionsMenu) {
      this.actionsMenu.openMenu();
    }
  }

  executeActivityAction(activityAction: ActivityAction) {
    switch (activityAction) {
      case 'pin':
        this.placeActivityOnPinbord();
        break;
      case 'unpin':
        this.removeActivityFromPinbord();
        break;
      case 'highlight':
        this.highlightActivity();
        break;
      case 'unHighlight':
        this.unHighlightActivity();
        break;
      case 'setRenewal':
        this.dialog.open(AddActivityImprovedDialogComponent, {
          data: {
            activityId: this.activity.id,
            improvedActivities: this.activity.improved_activities,
          },
        });
        break;
      case 'setRemoval':
        this.dialog.open(ManageActivityPendingRemovalComponent, {
          data: {
            activityId: this.activity.id,
            pendingRemovalActivities: this.activity.pending_removal_activities,
          },
        });
      default:
        break;
    }
  }

  async highlightActivity() {
    try {
      const currentOuId = await firstValueFrom(this.currentOUId$);
      await this.$activityHighLight.update(currentOuId, this.activity.id);
      if (!this.activity.highlighted_organization_unit_id) {
        this.activity.highlighted_organization_unit_id = [];
      }
      this.activity.highlighted_organization_unit_id.push(currentOuId);

      this.setHighlightActions();
    } catch (e) {
      // dont do anything yet
    }
  }

  async unHighlightActivity() {
    try {
      const currentOuId = await firstValueFrom(this.currentOUId$);
      await this.$activityHighLight.remove(currentOuId, this.activity.id);
      const ouIndex = this.activity.highlighted_organization_unit_id.indexOf(currentOuId);

      if (ouIndex > -1) {
        (this.activity as any).highlighted_organization_unit_id.splice(ouIndex, 1);
      }

      this.setHighlightActions();
    } catch (e) {
      // dont do anything yet
    }
  }

  async placeActivityOnPinbord() {
    this.dialog.open(AddActivityToPinbordFolderDialogComponent, {
      data: {
        activities: [this.activity],
      },
    });
  }

  async removeActivityFromPinbord() {
    this.dialog.open(RemoveActivityFromPinbordFolderDialogComponent, {
      data: {
        activity: this.activity,
      },
    });
  }
}
